import * as PIXI from 'pixi.js';
import type { TrackApiData, VisUnitApiData, ViewApiData } from '@gosling-lang/gosling-schema';
import type { HiGlassSpec } from '@gosling-lang/higlass-schema';
import type { HiGlassApi } from '../core/higlass-component-wrapper';
import { subscribe, unsubscribe } from './pubsub';
import { computeChromSizes, GenomicPositionHelper } from '../core/utils/assembly';
import type { CompleteThemeDeep } from '../core/utils/theme';
import type { IdTable } from './track-and-view-ids';

/**
 * Information of suggested genes.
 */
interface GeneSuggestion {
    geneName: string; // gene symbol
    score: number; // higher score means suggested gene is more likely to match the searched keyword
    chr: string; // chromosome name
    txStart: number; // absolute genomic position assuming chromosomes are concat end-to-end
    txEnd: number; // absolute genomic position assuming chromosomes are concat end-to-end
}

export interface GoslingApi {
    subscribe: typeof subscribe;
    unsubscribe: typeof unsubscribe;
    zoomTo(viewId: string, position: string, padding?: number, duration?: number): void;
    zoomToExtent(viewId: string, duration?: number): void;
    zoomToGene(viewId: string, gene: string, padding?: number, duration?: number): void;
    suggestGene(viewId: string, keyword: string, callback: (suggestions: GeneSuggestion[]) => void): void;
    getTracksAndViews(): VisUnitApiData[];
    /**
     * Get an array of all available track IDs that are either specified by users or auto-generated by the compiler.
     * This can be used to call other API functions, e.g., `getTrack('track-1')`.
     */
    getTrackIds(): string[];
    getTracks(): TrackApiData[];
    getTrack(trackId: string): TrackApiData | undefined;
    getViews(): ViewApiData[];
    getView(viewId: string): ViewApiData | undefined;
    exportPng(transparentBackground?: boolean): void;
    exportPdf(transparentBackground?: boolean): void;
    getCanvas(options?: { resolution?: number; transparentBackground?: boolean }): {
        canvas: HTMLCanvasElement;
        canvasWidth: number;
        canvasHeight: number;
        resolution: number;
    };
}

export function createApi(
    hg: Readonly<HiGlassApi>,
    hgSpec: HiGlassSpec | undefined,
    tracksAndViews: readonly VisUnitApiData[],
    theme: Required<CompleteThemeDeep>,
    idTable: Readonly<IdTable>
): GoslingApi {
    const idTableCopy = structuredClone(idTable);
    /**
     * Get the HiGlass view ID from the Gosling track ID.
     */
    const getHgViewId = (trackId: string) => {
        const viewId = idTableCopy[trackId];
        if (!viewId) {
            console.warn(`Unable to find the track ID, named ${trackId}.`);
        }
        return viewId ?? trackId;
    };
    const getTrackIds = () => {
        return Object.keys(idTableCopy);
    };
    const getTracksAndViews = () => {
        return [...tracksAndViews];
    };
    const getTracks = () => {
        return [...getTracksAndViews().filter(d => d.type === 'track')] as TrackApiData[];
    };
    const getTrack = (trackId: string) => {
        const trackInfoFound = getTracks().find(d => d.id === trackId);
        if (!trackInfoFound) {
            console.warn(`[getTrack()] Unable to find a track using the ID (${trackId})`);
        }
        return trackInfoFound;
    };
    const getViews = () => {
        return [...getTracksAndViews().filter(d => d.type === 'view')] as ViewApiData[];
    };
    const getView = (viewId: string) => {
        const view = getViews().find(d => d.id === viewId);
        if (!view) {
            console.warn(`Unable to find a view with the ID of ${viewId}`);
        }
        return view;
    };
    const getCanvas: GoslingApi['getCanvas'] = options => {
        const resolution = options?.resolution ?? 4;
        const transparentBackground = options?.transparentBackground ?? false;

        const renderer = hg.pixiRenderer;
        const renderTexture = PIXI.RenderTexture.create({
            width: renderer.width / 2,
            height: renderer.height / 2,
            resolution
        });

        renderer.render(hg.pixiStage, renderTexture);

        const canvas = renderer.plugins.extract.canvas(renderTexture);

        // Set background color for the given theme in the gosling spec
        // Otherwise, it is transparent
        const canvasWithBg = document.createElement('canvas') as HTMLCanvasElement;
        canvasWithBg.width = canvas.width;
        canvasWithBg.height = canvas.height;

        const ctx = canvasWithBg.getContext('2d')!;
        if (!transparentBackground) {
            ctx.fillStyle = theme.root.background;
            ctx.fillRect(0, 0, canvasWithBg.width, canvasWithBg.height);
        }
        ctx.drawImage(canvas, 0, 0);

        return {
            canvas: canvasWithBg,
            resolution,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height
        };
    };
    return {
        subscribe,
        unsubscribe,
        zoomTo: (trackId, position, padding = 0, duration = 1000) => {
            // Accepted input: 'chr1' or 'chr1:1-1000'
            const assembly = getTrack(trackId)?.spec.assembly;
            const manager = GenomicPositionHelper.fromString(position);
            const absCoordinates = manager.toAbsoluteCoordinates(assembly, padding);
            const hgViewId = getHgViewId(trackId);
            hg.api.zoomTo(hgViewId, ...absCoordinates, ...absCoordinates, duration);
        },
        zoomToExtent: (trackId, duration = 1000) => {
            const assembly = getTrack(trackId)?.spec.assembly;
            const [start, end] = [0, computeChromSizes(assembly).total];
            const hgViewId = getHgViewId(trackId);
            hg.api.zoomTo(hgViewId, start, end, start, end, duration);
        },
        zoomToGene: (trackId, gene, padding = 0, duration = 1000) => {
            const hgViewId = getHgViewId(trackId);
            hg.api.zoomToGene(hgViewId, gene, padding, duration);
        },
        suggestGene: (trackId: string, keyword: string, callback: (suggestions: GeneSuggestion[]) => void) => {
            const hgViewId = getHgViewId(trackId);
            hg.api.suggestGene(hgViewId, keyword, callback);
        },
        getTrackIds,
        getTracksAndViews,
        getTracks,
        getTrack,
        getView,
        getViews,
        getCanvas,
        exportPng: transparentBackground => {
            const { canvas } = getCanvas({ resolution: 4, transparentBackground });
            canvas.toBlob(blob => {
                const a = document.createElement('a');
                document.body.append(a);
                a.download = 'gosling-visualization';
                a.href = URL.createObjectURL(blob!);
                a.click();
                a.remove();
            }, 'image/png');
        },
        exportPdf: async transparentBackground => {
            const { jsPDF } = await import('jspdf');
            const { canvas } = getCanvas({ resolution: 4, transparentBackground });
            const imgData = canvas.toDataURL('image/jpeg', 1);
            const pdf = new jsPDF({
                orientation: canvas.width < canvas.height ? 'p' : 'l',
                unit: 'pt',
                format: [canvas.width, canvas.height]
            });
            pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
            pdf.save('gosling-visualization.pdf');
        }
    };
}
